import {
  Badge as BadgeIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  InputAdornment,
  TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { prescriptionApi } from "apis";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import FilterDropdown, { FilterGroup } from "components/common/FilterDropdown";
import { StatusTag } from "components/features/prescription/StatusTag";
import { DashboardLayout } from "components/layouts";
import { EPath } from "constants/path";
import { useToast } from "contexts/ToastContext";
import {
  PrescriptionResponseDto,
  PrescriptionResponseDtoStatusEnum,
  GetPrescriptionsForAdminSortByEnum,
} from "libs/cellera-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useCelleraTheme } from "theme/useCelleraTheme";

const filterConstants = {
  paid: {
    value: PrescriptionResponseDtoStatusEnum.Paid,
    label: PrescriptionResponseDtoStatusEnum.Paid.toUpperCase(),
  },
  pending: {
    value: PrescriptionResponseDtoStatusEnum.Pending,
    label: PrescriptionResponseDtoStatusEnum.Pending.toUpperCase(),
  },
  cancelled: {
    value: PrescriptionResponseDtoStatusEnum.Cancelled,
    label: PrescriptionResponseDtoStatusEnum.Cancelled.toUpperCase(),
  },
};

export const PrescriptionManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const { colors } = useCelleraTheme();
  const [searchValue, setSearchValue] = useState("");
  const [request, setRequest] = useState({
    page: 1,
    pageSize: 10,
    search: "",
    practiceIds: undefined as string | undefined,
    statuses: undefined as string | undefined,
    sortBy: undefined as GetPrescriptionsForAdminSortByEnum | undefined,
    orderBy: undefined as string | undefined,
  });

  // Debounce search input with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setRequest((prev) => ({ ...prev, search: searchValue, page: 1 }));
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const {
    data: prescriptionsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["prescriptions", request],
    queryFn: async () => {
      const response = await prescriptionApi.getPrescriptionsForAdmin({
        page: request.page,
        search: request.search,
        practiceIds: request.practiceIds || undefined,
        pageSize: request.pageSize,
        statuses: request.statuses,
        sortBy: request.sortBy,
        orderBy: request.orderBy?.toUpperCase(),
      });
      return {
        items: response.items,
        totalCount: response.total,
      };
    },
    retry: 2,
  });

  const totalCount = prescriptionsData?.totalCount || 0;
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPrescription, setSelectedPrescription] =
    useState<PrescriptionResponseDto | null>(null);

  const formatPrice = useCallback((price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  }, []);

  const formatPatientName = useCallback(
    (prescription: PrescriptionResponseDto) => {
      const text = [prescription.patientFirstName, prescription.patientLastName]
        .filter(Boolean)
        .join(" ");
      if (!text) return "N/A";
      return text;
    },
    [],
  );

  const formatProviderName = useCallback(
    (prescription: PrescriptionResponseDto) => {
      return `Dr. ${prescription.providerFirstName} ${prescription.providerLastName}`;
    },
    [],
  );

  const columns: CustomTableColumn<PrescriptionResponseDto>[] = useMemo(
    () => [
      {
        id: "id",
        label: "ID",
        getValue: (row) => row.id,
        sortable: false,
        sortAccessor: (row) => row.id,
      },
      {
        id: "patient",
        label: "Patient",
        getValue: (row) => (
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: colors.text.primary }}
            >
              {formatPatientName(row)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.text.secondary, fontSize: "0.75rem" }}
            >
              {row.patientEmail}
            </Typography>
          </Box>
        ),
        sortable: true,
        sortAccessor: (row) => `${row.patientFirstName} ${row.patientLastName}`,
      },
      {
        id: "datetime",
        label: "Datetime",
        getValue: (row) => {
          const date = new Date(row.createdAt);
          return date.toLocaleString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
        sortable: true,
        sortAccessor: (row) => row.createdAt,
      },
      {
        id: "price",
        label: "Price",
        getValue: (row) => formatPrice(row.price),
        sortable: true,
        sortAccessor: (row) => row.price,
      },
      {
        id: "prescriber",
        label: "Prescriber",
        getValue: (row) => (
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: colors.text.primary }}
            >
              {formatProviderName(row)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.text.secondary, fontSize: "0.75rem" }}
            >
              {row.providerEmail}
            </Typography>
          </Box>
        ),
        sortable: false,
      },
      {
        id: "practice",
        label: "Practice",
        getValue: (row) => (
          <Typography variant="body2" sx={{ color: colors.text.primary }}>
            {row.practiceName || "N/A"}
          </Typography>
        ),
        sortable: false,
      },
      {
        id: "status",
        label: "Status",
        getValue: (row) => <StatusTag status={row.status} />,
        sortable: true,
        sortAccessor: (row) => row.status,
      },
      {
        id: "actions",
        label: "",
        getValue: (row) => (
          <IconButton
            size="small"
            onClick={(event) => handleMenuClick(event, row)}
            sx={{
              color: colors.text.secondary,
              "&:hover": {
                backgroundColor: colors.background.secondary,
                color: colors.text.primary,
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [colors],
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
    },
    [],
  );

  // Handle query errors
  useEffect(() => {
    if (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch data";
      showError("Error", errorMessage);
    }
  }, [error, showError]);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRequest((prev) => {
        return {
          ...prev,
          pageSize: parseInt(event.target.value, 10),
          page: 1,
        };
      });
    },
    [],
  );

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setRequest((prev) => ({ ...prev, page: newPage + 1 }));
  }, []);

  const handleMenuClick = useCallback(
    (
      event: React.MouseEvent<HTMLElement>,
      prescription: PrescriptionResponseDto,
    ) => {
      setSelectedPrescription(prescription);
      setMenuAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedPrescription(null);
  }, []);

  // Extract unique practices from prescription data
  const practiceOptions = useMemo(() => {
    if (!prescriptionsData?.items) return [];

    const practiceMap = new Map<string, { id: string; name: string }>();
    prescriptionsData.items.forEach((item) => {
      if (
        item.practiceId &&
        item.practiceName &&
        !practiceMap.has(item.practiceId)
      ) {
        practiceMap.set(item.practiceId, {
          id: item.practiceId,
          name: item.practiceName,
        });
      }
    });

    return Array.from(practiceMap.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((practice) => ({
        value: practice.id,
        label: practice.name,
      }));
  }, [prescriptionsData?.items]);

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        label: "Status",
        key: "status",
        options: [
          filterConstants.paid,
          filterConstants.pending,
          filterConstants.cancelled,
        ],
      },
      {
        label: "Practice",
        key: "practice",
        options: practiceOptions,
      },
    ],
    [practiceOptions],
  );

  const currentFilters = useMemo(() => {
    const filters: Record<string, string[]> = {};
    if (request.statuses) {
      filters.status = request.statuses.split(",").map((s) => s.trim());
    }
    if (request.practiceIds) {
      filters.practice = request.practiceIds.split(",").map((id) => id.trim());
    }
    return filters;
  }, [request.statuses, request.practiceIds]);

  const handleFilterApply = (filters: Record<string, string[]>) => {
    const statuses = filters.status?.length
      ? filters.status.join(",")
      : undefined;
    const practiceIds = filters.practice?.length
      ? filters.practice.join(",")
      : undefined;

    setRequest((prev) => ({
      ...prev,
      statuses,
      practiceIds,
      page: 1,
    }));
  };

  // Map column IDs to API sortBy enum values
  const getSortByValue = (
    columnId: string,
  ): GetPrescriptionsForAdminSortByEnum | undefined => {
    const columnToSortByMap: Record<
      string,
      GetPrescriptionsForAdminSortByEnum
    > = {
      id: GetPrescriptionsForAdminSortByEnum.Id,
      patient: GetPrescriptionsForAdminSortByEnum.Patient,
      datetime: GetPrescriptionsForAdminSortByEnum.DateTime,
      status: GetPrescriptionsForAdminSortByEnum.Status,
      price: GetPrescriptionsForAdminSortByEnum.Price,
    };
    return columnToSortByMap[columnId];
  };

  const handleSortChange = useCallback(
    (columnId: string, direction: "asc" | "desc") => {
      const sortByValue = getSortByValue(columnId);
      if (sortByValue) {
        setRequest((prev) => ({
          ...prev,
          sortBy: sortByValue,
          orderBy: direction,
          page: 1, // Reset to first page when sorting changes
        }));
      }
    },
    [],
  );

  const getColumnIdFromSortBy = useCallback(
    (sortBy: GetPrescriptionsForAdminSortByEnum | undefined): string | null => {
      if (!sortBy) return null;

      const enumKey = Object.keys(GetPrescriptionsForAdminSortByEnum).find(
        (key) =>
          GetPrescriptionsForAdminSortByEnum[
            key as keyof typeof GetPrescriptionsForAdminSortByEnum
          ] === sortBy,
      );

      // Map enum keys to column IDs
      const enumToColumnMap: Record<string, string> = {
        Id: "id",
        Patient: "patient",
        DateTime: "datetime",
        Status: "status",
        Price: "price",
      };

      return enumKey ? enumToColumnMap[enumKey] || null : null;
    },
    [],
  );

  const emptyStateContent = useMemo(
    () => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <BadgeIcon
          sx={{
            color: "text.disabled",
            fontSize: 48,
            opacity: 0.5,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            fontWeight: 500,
          }}
        >
          No prescriptions available
        </Typography>
      </Box>
    ),
    [],
  );

  return (
    <DashboardLayout title="Prescription Management">
      <Box
        sx={{
          flex: 1,
          display: "flex",
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.border.light}`,
          borderRadius: "8px",
          p: 0,
          height: "calc(100vh - 100px)",
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box
            component="div"
            className="flex !w-full !py-3 !px-4"
            justifyContent="space-between"
            sx={{ gap: 2 }}
          >
            {/* Search Input */}
            <TextField
              fullWidth
              placeholder="Search"
              value={searchValue}
              onChange={handleSearchChange}
              size="small"
              sx={{ width: "300px" }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: colors.text.secondary }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <FilterDropdown
              filterGroups={filterGroups}
              currentFilters={currentFilters}
              onApply={handleFilterApply}
            />
          </Box>
          <CustomTable
            columns={columns}
            data={prescriptionsData?.items ?? []}
            totalCount={totalCount}
            page={(request.page ?? 1) - 1}
            isLoading={isLoading}
            rowsPerPage={request.pageSize ?? 10}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onSortChange={handleSortChange}
            sortBy={getColumnIdFromSortBy(request.sortBy)}
            sortDirection={(request.orderBy as "asc" | "desc") || undefined}
            disableClientSideSort
            loadingMessage="Loading prescription data..."
            emptyStateContent={emptyStateContent}
            paperSx={{
              height: "calc(100% - 80px)",
              borderRadius: 0,
              border: "none",
              justifyContent: "space-between",
            }}
          />
        </Box>
      </Box>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "8px",
              border: `1px solid ${colors.border.light}`,
              minWidth: 160,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate(
              EPath.PRESCRIPTION_DETAIL.replace(
                ":prescriptionId",
                selectedPrescription?.id ?? "",
              ),
            );
          }}
          sx={{
            color: colors.text.primary,
            "&:hover": {
              backgroundColor: colors.background.secondary,
            },
          }}
        >
          <ListItemText primary="View details" />
        </MenuItem>
      </Menu>
    </DashboardLayout>
  );
};

export default PrescriptionManagementPage;
